import { useQuery, ApolloError } from "@apollo/client";
import { useMemo } from "react";
import { COMMIT_INDEX_SUMMARY } from "@/api/queries/commitIndexSummary";
import { RISE_APPLY_INDEX_SUMMARY } from "@/api/queries/riseApplyIndexSummary";
import { RISE_COMMIT_INDEX_SUMMARY } from "@/api/queries/riseCommitIndexSummary";
import { RISE_MELT_INDEX_SUMMARY } from "@/api/queries/riseMeltIndexSummary";
import { RISE_YIELD_INDEX_SUMMARY } from "@/api/queries/riseYieldIndexSummary";
import { FunnelStateEnum, YesNoEnum } from "@/__generated__/types";

export type IndexType =
  | "commit"
  | "riseCommit"
  | "riseApply"
  | "riseYield"
  | "riseMelt";

type FunnelStateData = { state: FunnelStateEnum; value: number }[];

interface IndexSummaryData {
  low: FunnelStateData | null;
  mediumLow?: FunnelStateData | null;
  medium: FunnelStateData | null;
  mediumHigh?: FunnelStateData | null;
  high: FunnelStateData | null;
}

interface UseIndexSummaryReturn {
  data: IndexSummaryData | null;
  loading: boolean;
  error: ApolloError | undefined;
}

export const useIndexSummary = (
  indexType: IndexType,
  orgId: string | undefined,
  cohortYear: number | undefined,
  inCommunity?: YesNoEnum,
): UseIndexSummaryReturn => {
  const shouldSkip = !orgId || !cohortYear;

  const getQueryConfig = () => {
    const isCommit = indexType === "commit";
    const variables = !shouldSkip
      ? isCommit
        ? { orgId, cohortYear }
        : { orgId, cohortYear, inCommunity }
      : undefined;

    const queryMap = {
      commit: COMMIT_INDEX_SUMMARY,
      riseApply: RISE_APPLY_INDEX_SUMMARY,
      riseCommit: RISE_COMMIT_INDEX_SUMMARY,
      riseMelt: RISE_MELT_INDEX_SUMMARY,
      riseYield: RISE_YIELD_INDEX_SUMMARY,
    };

    return {
      query: queryMap[indexType],
      variables,
    };
  };

  const { query, variables } = getQueryConfig();

  const { data, loading, error } = useQuery(query, {
    skip: shouldSkip,
    variables,
  });

  const transformedData = useMemo<IndexSummaryData | null>(() => {
    if (!data?.node || data.node.__typename !== "Organization") {
      return null;
    }

    if (indexType === "commit") {
      const orgNode = data.node as typeof data.node & {
        commitIndexSummary?: {
          low?: FunnelStateData | null;
          medium?: FunnelStateData | null;
          high?: FunnelStateData | null;
        } | null;
      };

      const summary = orgNode.commitIndexSummary;
      if (!summary) return null;

      return {
        low: summary.low || null,
        medium: summary.medium || null,
        high: summary.high || null,
      };
    }

    const orgNode = data.node as typeof data.node & {
      rise?: {
        applyIndexSummary?: IndexSummaryData;
        commitIndexSummary?: IndexSummaryData;
        meltIndexSummary?: IndexSummaryData;
        yieldIndexSummary?: IndexSummaryData;
      } | null;
    };

    const riseNode = orgNode.rise;

    let summary;
    switch (indexType) {
      case "riseApply":
        summary = riseNode?.applyIndexSummary;
        break;
      case "riseCommit":
        summary = riseNode?.commitIndexSummary;
        break;
      case "riseMelt":
        summary = riseNode?.meltIndexSummary;
        break;
      case "riseYield":
        summary = riseNode?.yieldIndexSummary;
        break;
    }

    if (!summary) return null;

    return {
      low: summary.low || null,
      mediumLow: summary.mediumLow || null,
      medium: summary.medium || null,
      mediumHigh: summary.mediumHigh || null,
      high: summary.high || null,
    };
  }, [indexType, data]);

  return {
    data: transformedData,
    loading,
    error,
  };
};
